/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceProvenance.module.scss';

type Translate = (key: string) => string;

type ProvenanceRow = {
  k: string;
  v: string;
  mono?: boolean;
  modelled?: boolean;
  flag?: string;
  src: string;
  ts: string;
};

const getProvenanceRows = (t: Translate): ProvenanceRow[] => [
  {
    k: t('Registered name'),
    v: 'ACME LOGISTICS HOLDINGS LTD',
    flag: '🇬🇧',
    src: 'Companies House · company record',
    ts: '28 Jul 2026 06:14',
  },
  {
    k: t('Principal activity'),
    v: 'SIC 38220, hazardous waste',
    flag: '🇬🇧',
    src: 'Confirmation statement CS01',
    ts: '28 Jul 2026 06:14',
  },
  {
    k: t('Turnover FY2025'),
    v: '£11,840,000',
    mono: true,
    flag: '🇬🇧',
    src: 'Filed accounts · AA',
    ts: '28 Jul 2026 06:14',
  },
  {
    k: t('Ultimate parent'),
    v: 'MERIDIAN HOLDINGS S.à r.l.',
    flag: '🇱🇺',
    src: 'RCS · shareholding filing',
    ts: '28 Jul 2026 06:11',
  },
  {
    k: t('Beneficial owner'),
    v: t('Natural person · 82% indirect'),
    flag: '🇱🇺',
    src: 'RBE beneficial owners',
    ts: '28 Jul 2026 06:11',
  },
  {
    k: t('Credit assessment'),
    v: t('Modelled, not a filed figure'),
    modelled: true,
    src: 'Global Database model v4',
    ts: '28 Jul 2026 06:14',
  },
];

type Obligation = {
  title: string;
  flags: string[];
  jr: string;
  body: string;
  status: 'Live' | 'Rev' | 'Hz';
  statusText: string;
};

const getObligations = (t: Translate): Obligation[] => [
  {
    title: 'Solvency II, Article 82',
    flags: ['🇫🇷'],
    jr: 'EU · 2009/138/EC',
    body: t(
      'Undertakings must have processes ensuring the appropriateness, completeness and accuracy of data used in technical provisions. Field-level attribution shows where each input came from and when.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: 'Solvency UK',
    flags: ['🇬🇧'],
    jr: 'UK · PRA rulebook',
    body: t(
      'The reformed UK regime carries the same data quality expectations through to firms in scope. Confirm current rulebook references with compliance before publication.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: 'Insurance Distribution Directive',
    flags: ['🇩🇪'],
    jr: 'EU · 2016/97',
    body: t(
      'Distribution arrangements require ongoing oversight of intermediaries. Registry monitoring surfaces changes in control, officers and registered status between reviews.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: t('Delegated authority due diligence'),
    flags: ['🇬🇧'],
    jr: "Lloyd's market",
    body: t(
      'Coverholder and delegated authority arrangements carry due diligence and ongoing oversight expectations. Primary entity records give one evidence base across every binder.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: t('Financial sanctions'),
    flags: ['🇬🇧', '🇺🇸'],
    jr: 'OFSI · OFAC · EU',
    body: t(
      'Financial sanctions obligations apply to every firm, whatever its money laundering scope, and reach the provision of cover and the payment of claims alike.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: 'OFAC 50 Percent Rule',
    flags: ['🇺🇸'],
    jr: 'US · OFAC guidance',
    body: t(
      'Entities owned 50% or more in aggregate, directly or indirectly, by one or more blocked persons are themselves blocked whether or not they appear on the SDN list. The rule turns on ownership, not control, so it can only be applied against resolved holdings.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: t('Ownership and control test'),
    flags: ['🇬🇧'],
    jr: 'UK · OFSI',
    body: t(
      'Ownership turns on more than 50% of shares or voting rights, or the right to appoint or remove a majority of the board. A separate control limb applies. OFSI closed a call for evidence on the test in June 2026 and is considering whether further clarification is needed; existing guidance continues to apply.',
    ),
    status: 'Rev',
    statusText: t('Under review'),
  },
  {
    title: t('Maritime sector guidance'),
    flags: ['🇬🇧'],
    jr: 'UK · OFSI maritime',
    body: t(
      "OFSI's sector guidance extends expressly to insurers in maritime shipping, alongside the separate oil price cap and maritime services regime. Designations have stranded live policies, wound down under general licence.",
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: 'Money Laundering Regulations 2017',
    flags: ['🇬🇧'],
    jr: 'UK · life and long-term',
    body: t(
      'The UK regulated sector covers life and long-term investment insurance rather than general insurance. Where the regime applies, beneficial ownership is evidenced from registry filings.',
    ),
    status: 'Live',
    statusText: t('In force'),
  },
  {
    title: t('Corporate sustainability reporting'),
    flags: ['🇮🇹'],
    jr: 'EU · CSRD, CSDDD',
    body: t(
      'Scope and timing narrowed under the Omnibus package, with due diligence application pushed out. Treated here as horizon rather than a current obligation.',
    ),
    status: 'Hz',
    statusText: t('Horizon'),
  },
];

export const InsuranceProvenance = () => {
  const { t } = useInt();
  const pvRef = useRef<HTMLTableSectionElement>(null);
  const pvRevealed = useIntersection(pvRef, { threshold: 0.08 });
  const obRef = useRef<HTMLTableSectionElement>(null);
  const obRevealed = useIntersection(obRef, { threshold: 0.08 });

  const provenanceRows = getProvenanceRows(t);
  const obligations = getObligations(t);

  return (
    <Section
      background="radial-gradient(ellipse 55% 55% at 88% 0%, rgba(51, 92, 244, 0.4), transparent 70%), radial-gradient(ellipse 45% 45% at 5% 100%, rgba(1, 83, 244, 0.28), transparent 70%), radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.06) 1px, transparent 0) 0 0 / 28px 28px, #042354"
      className={styles.root}
    >
      <div className={styles.head}>
        <TitleBlock theme="dark" title={t('Every field traces back to the filing it came from.')} />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.tbl}>
          <thead>
            <tr>
              <th>{t('Field')}</th>
              <th>{t('Value')}</th>
              <th>{t('Source')}</th>
              <th>{t('Retrieved')}</th>
            </tr>
          </thead>
          <tbody ref={pvRef}>
            {provenanceRows.map((row, i) => (
              <tr
                className={cn(styles.row, pvRevealed && styles.rowIn)}
                style={{ transitionDelay: `${i * 45}ms` }}
                key={row.k}
              >
                <td className={styles.k}>{row.k}</td>
                <td className={cn(styles.v, row.mono && styles.mono, row.modelled && styles.modelled)}>{row.v}</td>
                <td>
                  <span className={cn(styles.src, row.modelled && styles.modelled)}>
                    {row.flag && <span className={styles.flag}>{row.flag}</span>} {row.src}
                  </span>
                </td>
                <td className={styles.ts}>{row.ts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.note}>
        {t(
          'Modelled fields are labelled as modelled everywhere they appear. A filed figure is never presented as anything but a filed figure.',
        )}
      </p>

      <div className={styles.head2}>
        <TitleBlock theme="dark" title={t('And it holds when a regulator asks where the number came from.')} />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.tbl}>
          <thead>
            <tr>
              <th className={styles.wRegime}>{t('Regime')}</th>
              <th className={styles.wJur}>{t('Jurisdiction')}</th>
              <th>{t('Why it reaches company data')}</th>
              <th className={styles.wStatus}>{t('Status')}</th>
            </tr>
          </thead>
          <tbody ref={obRef}>
            {obligations.map((row, i) => (
              <tr
                className={cn(styles.row, obRevealed && styles.rowIn)}
                style={{ transitionDelay: `${i * 45}ms` }}
                key={row.title}
              >
                <td>
                  <h4 className={styles.obTitle}>{row.title}</h4>
                </td>
                <td>
                  <span className={styles.jr}>
                    {row.flags.map((flag) => (
                      <span className={styles.flag} key={flag}>
                        {flag}
                      </span>
                    ))}
                    {row.jr}
                  </span>
                </td>
                <td>
                  <p className={styles.obBody}>{row.body}</p>
                </td>
                <td>
                  <span className={cn(styles.badge, styles[`badge${row.status}`])}>
                    {row.status === 'Live' && <i className={styles.badgeDot} />}
                    {row.statusText}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};
